import React, { useEffect, useState } from "react";
import { Save, Loader2, Plus, Trash2 } from "lucide-react";
import { fetchSettings, patchSettings, img } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import ImageUpload from "../ImageUpload";
import { toast } from "../../hooks/use-toast";

const input = "w-full bg-navy-700 border border-white/15 focus:border-gold rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 outline-none transition-colors";
const label = "block text-xs text-slate-400 mb-1.5";

const ShippingPanel = () => {
  const { reload } = useSite();
  const [methods, setMethods] = useState(null);
  const [carriers, setCarriers] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSettings().then((s) => { setMethods(s.shippingMethods || []); setCarriers(s.carriers || []); }).catch(() => {}); }, []);
  if (!methods || !carriers) return <div className="text-slate-400">Loading...</div>;

  const updM = (i, k, v) => setMethods((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const updC = (i, k, v) => setCarriers((p) => p.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));

  const save = async () => {
    setSaving(true);
    try {
      const clean = methods.map((m) => ({ ...m, price: Number(m.price) || 0 }));
      await patchSettings({ shippingMethods: clean, carriers });
      await reload();
      toast({ title: "Saved", description: "Shipping settings updated." });
    } catch { toast({ title: "Error", description: "Could not save." }); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold text-lg">Shipping Methods</h3>
          <button onClick={() => setMethods((p) => [...p, { id: "ship-" + Date.now(), name: "", desc: "", price: 0 }])} className="inline-flex items-center gap-2 text-sm text-gold hover:underline"><Plus size={14} /> Add</button>
        </div>
        <div className="space-y-3">
          {methods.map((m, i) => (
            <div key={m.id} className="bg-navy-800 rounded-xl border border-white/10 p-4 grid sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-4"><label className={label}>Name</label><input className={input} value={m.name} onChange={(e) => updM(i, "name", e.target.value)} /></div>
              <div className="sm:col-span-5"><label className={label}>Description</label><input className={input} value={m.desc} onChange={(e) => updM(i, "desc", e.target.value)} placeholder="2 business days. Tracked" /></div>
              <div className="sm:col-span-2"><label className={label}>Price ($)</label><input type="number" className={input} value={m.price} onChange={(e) => updM(i, "price", e.target.value)} /></div>
              <div className="sm:col-span-1 flex justify-end"><button onClick={() => setMethods((p) => p.filter((_, idx) => idx !== i))} className="h-9 w-9 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 transition-colors"><Trash2 size={15} /></button></div>
            </div>
          ))}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-white font-bold text-lg">Carriers</h3>
          <button onClick={() => setCarriers((p) => [...p, { id: "car-" + Date.now(), name: "", logo: "" }])} className="inline-flex items-center gap-2 text-sm text-gold hover:underline"><Plus size={14} /> Add</button>
        </div>
        <div className="space-y-3">
          {carriers.map((c, i) => (
            <div key={c.id} className="bg-navy-800 rounded-xl border border-white/10 p-4 flex items-end gap-3">
              <div className="h-11 w-11 rounded-lg bg-navy-700 flex items-center justify-center shrink-0">{c.logo ? <img src={img(c.logo)} alt="" className="h-6" /> : <span className="text-slate-600 text-[10px]">logo</span>}</div>
              <div className="w-40"><label className={label}>Carrier Name</label><input className={input} value={c.name} onChange={(e) => updC(i, "name", e.target.value)} /></div>
              <div className="flex-1"><label className={label}>Logo</label><div className="flex items-center gap-2"><ImageUpload value={c.logo} onUpload={(url) => updC(i, "logo", url)} onClear={() => updC(i, "logo", "")} label="Upload" /><input className={`${input} flex-1`} value={c.logo || ""} onChange={(e) => updC(i, "logo", e.target.value)} placeholder="or logo URL" /></div></div>
              <button onClick={() => setCarriers((p) => p.filter((_, idx) => idx !== i))} className="h-9 w-9 rounded-lg border border-white/15 text-slate-400 flex items-center justify-center hover:text-red-400 transition-colors"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>

      <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 bg-gold hover:bg-gold-light text-navy font-semibold px-6 py-3 rounded-xl transition-colors disabled:opacity-60">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Changes</button>
    </div>
  );
};

export default ShippingPanel;
